import { ContactInfo, Note } from "../types/listing";
import { EnumProductTypes } from "../types/listing";
import {
  PreforeclosureStatusTypeEnum,
  StatusTypeEnum,
} from "../data/selectionData";
import { formatDate } from "./dates";

import { Listing } from "src/types/listing";
import { LISTING_STATUS, LISTING_STATUS_COLORS } from '../constants/listing';
import { COLORS } from "../constants/colors";

export type DistressRateInfo = {
  rate: number;
  description: "Low" | "Medium" | "High" | "Very High";
};

export const getDistressedListingRateAsWord = (listing: Listing): DistressRateInfo => {
  const rate = Number(listing.distressedLevel) || 0;
  if (rate > 3) {
    return { rate, description: "Very High" };
  } else if (rate > 2) {
    return { rate, description: "High" };
  } else if (rate > 1) {
    return { rate, description: "Medium",};
  } else {
    return { rate, description: "Low", };
  }
};

export const getDistressedListingMapMarkerColor = (rate: number) => {
  if (rate === 0) {
    return "#F5F7F7";
  } else if (rate === 1) {
    return "#F6E3D2";
  } else if (rate === 2) {
    return "#FDCFB8";
  } else if (rate === 3) {
    return "#F6A77F";
  } else if (rate === 4) {
    return COLORS.PRIMARY;
  }
  return null;
};

export const getExpiredListingMapMarkerColor = (status: string) => {
  if (["X", "W", "C"].includes(status)) {
    return COLORS.SUCCESS;
  }

  if (["C"].includes(status)) {
    return COLORS.DANGER;
  }

  return null;
};

export const getPreForeclosureListingMapMarkerColor = (status: string) => {
  /* eslint-disable no-fallthrough */
  switch (status) {
    // Lis Pendens
    case "L":
    // Notice of Default
    case "D":
      // Green
      return COLORS.SUCCESS_LIGHT;
    // Notice of Sale
    case "S":
    // Notice of Trustee Sale
    case "T":
      // Orange
      return COLORS.WARNING_LIGHT;
    // Bank Owned (REO)
    case "B":
      // Blue
      return COLORS.SUCCESS;
    default:
      return null;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ServerListing = Record<string, any>;

export const getStatusDateFromMlsStatus = (listing: ServerListing) =>
  listing?.mls_status?.status_date;

export const getListingStatusDate = (listing: ServerListing) =>
  listing?.status_date;

export const getListingStatusDateUsingListingType = (
  listing: ServerListing
) => {
  const { productType } = listing;
  switch (productType) {
    case EnumProductTypes.preforeclosure:
    case EnumProductTypes.fsbo:
    case EnumProductTypes.distressed:
      return getStatusDateFromMlsStatus(listing);
    case EnumProductTypes.expired:
    case EnumProductTypes.oldexpired:
      return getListingStatusDate(listing);
    default:
      return null;
  }
};

export const getPreForeclosureListingStatus = (listing: ServerListing) => {
  return listing?.mls_status
    ? StatusTypeEnum[listing.mls_status?.status as keyof typeof StatusTypeEnum]
    : PreforeclosureStatusTypeEnum?.[
        listing?.listing_type as keyof typeof PreforeclosureStatusTypeEnum
      ];
};

export const getOldExpiredListingStatus = (listing: ServerListing) =>
  StatusTypeEnum[listing?.status as keyof typeof StatusTypeEnum];

export const getFsboListingStatus = (listing: ServerListing) =>
  StatusTypeEnum[listing?.mls_status?.status as keyof typeof StatusTypeEnum] ||
  "FSBO";

export const getDistressedListingStatus = (listing: ServerListing) =>
  listing?.mls_status?.status
    ? StatusTypeEnum[listing.mls_status.status as keyof typeof StatusTypeEnum]
    : "";

export const getExpiredListingStatus = (listing: ServerListing) =>
  StatusTypeEnum[listing?.status as keyof typeof StatusTypeEnum];

export const getListingStatusUsingListingType = (listing: ServerListing) => {
  const { productType } = listing;
  switch (productType) {
    case EnumProductTypes.preforeclosure:
      return getPreForeclosureListingStatus(listing);
    case EnumProductTypes.fsbo:
      return getFsboListingStatus(listing);
    case EnumProductTypes.distressed:
      return getDistressedListingStatus(listing);
    case EnumProductTypes.expired:
      return getExpiredListingStatus(listing);
    case EnumProductTypes.oldexpired:
      return getOldExpiredListingStatus(listing);
    default:
      return null;
  }
};


export const getListingTitleUsingListingType = (listing: ServerListing) => {
  const { productType } = listing;
  switch (productType) {
    case EnumProductTypes.preforeclosure:
      return getPreforeclosureListingTitle(listing);
    case EnumProductTypes.distressed:
      return getDistressedListingTitle(listing);
    case EnumProductTypes.expired:
    case EnumProductTypes.oldexpired:
      return getExpiredListingTitle(listing);
    case EnumProductTypes.fsbo:
      return getFsboListingTitle(listing);
    default:
      return "";
  }
};

export const getFormattedAddress = (
  streetAddress?: string | null,
  city?: string | null,
  state?: string | null,
  postalCode?: string | null,
  unitNumber?: string | null,
  fallbackIfNoStreet?: string
) => {
  if (!streetAddress) {
    return fallbackIfNoStreet || `--, ${city || "--"}, ${state || "--"} ${postalCode || "--"}`;
  }
  
  const unitPart = unitNumber ? ` Unit ${unitNumber}` : "";
  return `${streetAddress}${unitPart}, ${city || "--"}, ${state || "--"} ${postalCode || "--"}`;
};

export const getExpiredListingTitle = (listing: ServerListing) =>
  getFormattedAddress(
    listing?.property?.street_address || listing?.contacts?.street_address,
    listing?.property?.city || listing?.contacts?.city,
    listing?.property?.state || listing?.contacts?.state,
    listing?.property?.postal_code || listing?.contacts?.postal_code
  );

export const getDistressedListingTitle = (listing: ServerListing) =>
  getFormattedAddress(
    listing?.property?.street_address || listing?.street_address,
    listing?.property?.city || listing?.city,
    listing?.property?.state || listing?.state,
    listing?.property?.postal_code || listing?.postal_code
  );

export const getImportedListingTitle = (listing: ServerListing) =>
  getFormattedAddress(
    listing?.property?.street_address,
    listing?.property?.city,
    listing?.property?.state,
    listing?.property?.postal_code,
    undefined,
    "No Address"
  );

export const getFsboListingTitle = (listing: ServerListing) =>
  getFormattedAddress(
    listing?.street_address,
    listing?.city,
    listing?.state,
    listing?.postal_code,
  );

export const getPreforeclosureListingTitle = (listing: ServerListing) =>
  getFormattedAddress(
    listing?.property?.street_address,
    listing?.property?.city,
    listing?.property?.state,
    listing?.property?.postal_code
  );

export const getListingPrice = ({ list_price }: ServerListing) =>
  parseFloat(list_price || 0);

export const getFsboListingPrice = (listing: ServerListing) =>
  parseFloat(listing?.price || 0);

export const getImportedListingPrice = (listing: ServerListing) =>
  parseFloat(listing?.price || 0);

export const getDistressedListingPrice = (listing: ServerListing) =>
  parseFloat(
    listing?.list_price ||
      listing?.auction_min_bid ||
      listing?.market_value ||
      0
  );

export const getPreforeclosureListingPrice = (listing: ServerListing) =>
  parseFloat(listing?.list_price || listing?.auction_min_bid || 0);


export const getPropertyId = (listing: ServerListing) =>
  listing.productType === EnumProductTypes.fsbo
    ? listing?.property_id
    : listing?.property?.id;

export const getDisplayedContacts = (contacts: ContactInfo[]) => {
  return sortContacts(removeDuplicateContactsAndMarkOriginals(contacts));
};

export const removeDuplicateContactsAndMarkOriginals = (
  contacts: ContactInfo[]
) => {
  const uniqueContacts: ContactInfo[] = [];
  const seenNumbers = new Map();

  contacts.forEach((contact) => {
    if (!seenNumbers.has(contact?.phone)) {
      seenNumbers.set(contact?.phone, uniqueContacts?.length);
      uniqueContacts.push({ ...contact, isFavorite: false });
    } else {
      const originalIndex = seenNumbers.get(contact?.phone);
      const originalContact = uniqueContacts[originalIndex];

      // Check if the original contact's phone_type is 'U' or 'u'
      if (
        originalContact.phoneType === "U" ||
        originalContact?.phoneType === "u"
      ) {
        // Check if the current contact's phone_type is not 'U' or 'u'
        if (contact?.phoneType !== "U" && contact?.phoneType !== "u") {
          // Update the original contact's phone_type
          originalContact.phoneType = contact?.phoneType;
        }
      }

      // Mark the original contact with a star
      originalContact.isFavorite = true;
    }
  });

  return uniqueContacts;
};

export const sortContacts = (contacts: ContactInfo[]) => {
  // Stable sort: favorites first, otherwise preserve server order
  const indexed = contacts.map((contact, index) => ({ contact, index }));
  indexed.sort((a, b) => {
    if (a.contact.isFavorite && !b.contact.isFavorite) return -1;
    if (!a.contact.isFavorite && b.contact.isFavorite) return 1;
    return a.index - b.index;
  });
  return indexed.map(item => item.contact);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getClientNoteFromServerNote = (note: Record<string, any>): Note => {
  // Handle both field naming conventions:
  // - Standard listings use create_date/modify_date
  // - Neighborhood Search uses create_timestamp/modify_timestamp
  const createdAt = note?.create_date || note?.create_timestamp;
  const updatedAt = note?.modify_date || note?.modify_timestamp;

  return {
    id: note?.id,
    note: note?.note,
    createdAt,
    formattedCreatedAt: formatDate(createdAt),
    updatedAt,
    formattedUpdatedAt: formatDate(updatedAt),
  };
};

export const getListingStatusColors = (status: string): { color: string; textColor: string; bgColor: string } | null => {
  const lowerStatus = status.toLowerCase();
  
  if (lowerStatus === LISTING_STATUS.ACTIVE) {
    return {
      color: LISTING_STATUS_COLORS.ACTIVE.color,
      textColor: LISTING_STATUS_COLORS.ACTIVE.text,
      bgColor: LISTING_STATUS_COLORS.ACTIVE.background
    };
  }
  
  const greenStatuses = [
    LISTING_STATUS.EXPIRED,
    LISTING_STATUS.CANCELED,
    LISTING_STATUS.WITHDRAWN,
    LISTING_STATUS.LIS_PENDENS,
    LISTING_STATUS.NOTICE_OF_DEFAULT,
    LISTING_STATUS.FSBO
  ] as const;
  
  if (greenStatuses.includes(lowerStatus as typeof greenStatuses[number])) {
    return {
      color: LISTING_STATUS_COLORS.GREEN.color,
      textColor: LISTING_STATUS_COLORS.GREEN.text,
      bgColor: LISTING_STATUS_COLORS.GREEN.background
    };
  }
  
  const orangeStatuses = [
    LISTING_STATUS.NOTICE_OF_TRUSTEE_SALE,
    LISTING_STATUS.NOTICE_OF_SALE,
    LISTING_STATUS.PENDING,
    LISTING_STATUS.SOLD,
    LISTING_STATUS.OFF_MARKET,
    LISTING_STATUS.UNKNOWN
  ] as const;
  
  if (orangeStatuses.includes(lowerStatus as typeof orangeStatuses[number])) {
    return {
      color: LISTING_STATUS_COLORS.ORANGE.color,
      textColor: LISTING_STATUS_COLORS.ORANGE.text,
      bgColor: LISTING_STATUS_COLORS.ORANGE.background
    };
  }
  
  if (lowerStatus === LISTING_STATUS.BANK_OWNED) {
    return {
      color: LISTING_STATUS_COLORS.BANK_OWNED.color,
      textColor: LISTING_STATUS_COLORS.BANK_OWNED.text,
      bgColor: LISTING_STATUS_COLORS.BANK_OWNED.background
    };
  }
  
  return null;
};

export const DISTRESS_RATE_COLORS = {
  LEVEL_1: "#F6E3D2",
  LEVEL_2: "#FDCFB8",
  LEVEL_3: "#F6A77F",
  LEVEL_4: COLORS.PRIMARY,
  DEFAULT: "#F5F7F7"
} as const;

export const getDistressRateColor = (rate: number): string => {
  const parsedRate = parseInt(rate.toString());
  
  if (parsedRate > 3) return DISTRESS_RATE_COLORS.LEVEL_4;
  if (parsedRate > 2) return DISTRESS_RATE_COLORS.LEVEL_3;
  if (parsedRate > 1) return DISTRESS_RATE_COLORS.LEVEL_2;
  if (parsedRate > 0) return DISTRESS_RATE_COLORS.LEVEL_1;
  
  return DISTRESS_RATE_COLORS.DEFAULT;
};